import { HttpClient, httpResource } from '@angular/common/http';
import type { Signal } from '@angular/core';
import { inject, Injectable } from '@angular/core';

import type {
  AssetCountByConditionAndMaterialResponse,
  AssetCountByConditionAndRankResponse,
} from '../../domain/assets/asset-visualization.model';
import type {
  CurbAssetResponse,
  UpdateCurbAssetRequest,
} from '../../domain/assets/curb-asset.model';
import { assertIsDefined } from '../../domain/types';
import type { ChartData } from '../../domain/ui/chart-data.model';
import type { GaugeData } from '../../domain/ui/radial-gauge.model';
import { DataInvalidationService } from '../data-invalidation.service';
import { TenantContext } from '../tenant.context';

@Injectable({
  providedIn: 'root',
})
export class CurbAssetService {
  readonly #httpClient = inject(HttpClient);
  readonly #tenantContext = inject(TenantContext);
  readonly #dataInvalidationService = inject(DataInvalidationService);

  curbAssetResource(curbAssetId: Signal<string>) {
    return httpResource<CurbAssetResponse>(() => {
      const tenantId = this.#tenantContext.currentTenantId();
      const curbAssetIdValue = curbAssetId();
      return tenantId && curbAssetIdValue
        ? `api/tenants/${tenantId}/assets/curbs/${curbAssetIdValue}`
        : undefined;
    });
  }

  updateCurbAsset(assetId: string, request: UpdateCurbAssetRequest) {
    const tenantId = this.#tenantContext.currentTenantId();
    assertIsDefined(tenantId);
    return this.#httpClient.put<CurbAssetResponse>(
      `api/tenants/${tenantId}/assets/curbs/${assetId}`,
      request,
    );
  }

  curbAssetAverageConditionScoreResource() {
    return httpResource<GaugeData>(() => {
      this.#dataInvalidationService.track('assets');
      const tenantId = this.#tenantContext.currentTenantId();
      return tenantId
        ? `api/tenants/${tenantId}/assets/curbs/visualization/average-condition-score`
        : undefined;
    });
  }

  curbAssetCountByConditionResource() {
    return httpResource<ChartData[]>(() => {
      this.#dataInvalidationService.track('assets');
      const tenantId = this.#tenantContext.currentTenantId();
      return tenantId
        ? `api/tenants/${tenantId}/assets/curbs/visualization/count-by-condition`
        : undefined;
    });
  }

  curbAssetCenterlineMilesByMaterialResource() {
    return httpResource<ChartData[]>(() => {
      this.#dataInvalidationService.track('assets');
      const tenantId = this.#tenantContext.currentTenantId();
      return tenantId
        ? `api/tenants/${tenantId}/assets/curbs/visualization/centerline-miles-by-material`
        : undefined;
    });
  }

  curbAssetCountByConditionAndMaterialResource() {
    return httpResource<AssetCountByConditionAndMaterialResponse[]>(() => {
      this.#dataInvalidationService.track('assets');
      const tenantId = this.#tenantContext.currentTenantId();
      return tenantId
        ? `api/tenants/${tenantId}/assets/curbs/visualization/asset-count-by-condition-and-material`
        : undefined;
    });
  }

  curbAssetCountByConditionAndRankResource() {
    return httpResource<AssetCountByConditionAndRankResponse[]>(() => {
      this.#dataInvalidationService.track('assets');
      const tenantId = this.#tenantContext.currentTenantId();
      return tenantId
        ? `api/tenants/${tenantId}/assets/curbs/visualization/asset-count-by-condition-and-rank`
        : undefined;
    });
  }
}
